// =============================================
// AccountsTab — zakladka "Konta" w /cloudflare: lista kont CF (token per konto
// w DB cloudflare_accounts), dodawanie, edycja (nazwa / e-mail / Account ID /
// podmiana tokena z testem uprawnien), usuwanie.
// Wyniesione z CloudflarePage.tsx (2026-10-03) — logika bez zmian.
// =============================================
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Cloud, Plus, Trash2, Loader2, KeyRound, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCloudflareAccounts, type CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import { verifyToken, listAccounts } from "@/services/cloudflareService";
import { PermissionBadges, TokenCheckDialog, TokenCheckSteps } from "./TokenCheckDialog";
import { useTokenCheck } from "./useTokenCheck";

interface Props {
  accounts: CloudflareAccount[];
  loading: boolean;
  addAccount: ReturnType<typeof useCloudflareAccounts>["addAccount"];
  deleteAccount: ReturnType<typeof useCloudflareAccounts>["deleteAccount"];
  updateAccount: ReturnType<typeof useCloudflareAccounts>["updateAccount"];
}

export const AccountsTab = ({ accounts, loading, addAccount, deleteAccount, updateAccount }: Props) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", api_token: "", account_id: "", email: "" });
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  // Edycja istniejacego konta: nazwa / e-mail / Account ID / token (token opcjonalnie — puste = bez zmiany)
  const [editAcc, setEditAcc] = useState<CloudflareAccount | null>(null);
  const [editToken, setEditToken] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editAccountId, setEditAccountId] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  // Test nowego tokena w edycji — ta sama logika co "Sprawdz token" (runTokenCheck)
  const editCheck = useTokenCheck();
  // "Sprawdz token" przy koncie (dialog z uprawnieniami)
  const [checkAcc, setCheckAcc] = useState<CloudflareAccount | null>(null);

  const openEdit = (acc: CloudflareAccount) => {
    setEditAcc(acc);
    setEditToken("");
    setEditLabel(acc.label || "");
    setEditEmail(acc.email || "");
    setEditAccountId(acc.account_id || "");
    editCheck.reset();
  };
  const closeEdit = () => { setEditAcc(null); setEditToken(""); editCheck.reset(); };

  const handleEditVerify = async () => {
    const t = editToken.trim();
    if (!t) return;
    setEditBusy(true);
    try {
      const res = await editCheck.run({ api_token: t, account_id: editAccountId.trim() || editAcc?.account_id || "" });
      // auto-uzupelnienie Account ID, gdy token widzi dokladnie jedno konto
      if (!editAccountId.trim() && res.detectedAccountIds.length === 1) setEditAccountId(res.detectedAccountIds[0]);
    } finally {
      setEditBusy(false);
    }
  };

  const handleEditSave = async () => {
    if (!editAcc || !editLabel.trim()) return;
    setEditBusy(true);
    try {
      const patch: Partial<{ label: string; api_token: string; account_id: string; email: string }> = {
        label: editLabel.trim(),
        email: editEmail.trim(),
        account_id: editAccountId.trim(),
      };
      if (editToken.trim()) {
        patch.api_token = editToken.trim();
        if (!patch.account_id) {
          try { const accs = await listAccounts(editToken.trim()); if (accs.length === 1) patch.account_id = accs[0].id; } catch { /* ignore */ }
        }
      }
      const ok = await updateAccount(editAcc.id, patch);
      if (ok) {
        toast({ title: "Konto zaktualizowane", description: editToken.trim() ? `${patch.label} — nowy token zapisany` : patch.label });
        closeEdit();
      }
    } finally {
      setEditBusy(false);
    }
  };

  const handleVerify = async () => {
    if (!form.api_token.trim()) return;
    setVerifying(true);
    try {
      const res = await verifyToken(form.api_token.trim());
      if (res.ok) {
        try {
          const accs = await listAccounts(form.api_token.trim());
          if (accs.length === 1) setForm((f) => ({ ...f, account_id: accs[0].id }));
        } catch { /* brak uprawnien /accounts — nie blokujemy */ }
        toast({ title: "Token poprawny ✓", description: "Cloudflare przyjął token." });
      } else {
        toast({ title: "Token odrzucony", description: res.error, variant: "destructive" });
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleAdd = async () => {
    if (!form.label.trim() || !form.api_token.trim()) return;
    setSaving(true);
    const ok = await addAccount(form);
    setSaving(false);
    if (ok) { setForm({ label: "", api_token: "", account_id: "", email: "" }); setOpen(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Dodaj konta Cloudflare. Token API: <code>Zone:Read</code> + <code>DNS:Edit</code> (rekordy, kreator) + <code>Cloudflare Pages:Edit</code> (migracja stron). „Sprawdź token” pokazuje, które uprawnienia działają. Domeny i nameservery obejrzysz w zakładce <strong>Domeny w CF</strong>.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />Dodaj konto</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nowe konto Cloudflare</DialogTitle>
              <DialogDescription>
                Token: Cloudflare → My Profile → API Tokens → Create Token, uprawnienie <code>Zone:Edit</code>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cf-label">Nazwa (etykieta)</Label>
                <Input id="cf-label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="np. Konto główne / klient X" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-token">API Token</Label>
                <div className="flex gap-2">
                  <Input id="cf-token" type="password" value={form.api_token} onChange={(e) => setForm((f) => ({ ...f, api_token: e.target.value }))} placeholder="token Zone:Edit" className="font-mono" />
                  <Button type="button" variant="outline" onClick={handleVerify} disabled={verifying || !form.api_token.trim()}>
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sprawdź"}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-acc">Account ID <span className="text-muted-foreground">(wymagane do dodawania domen)</span></Label>
                <Input id="cf-acc" value={form.account_id} onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value }))} placeholder="autouzupełni się po „Sprawdź”" className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-email">E-mail konta <span className="text-muted-foreground">(opcjonalnie)</span></Label>
                <Input id="cf-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Anuluj</Button>
              <Button onClick={handleAdd} disabled={saving || !form.label.trim() || !form.api_token.trim()}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Zapisz konto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : accounts.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Cloud className="h-8 w-8 mx-auto mb-3 opacity-40" />Brak kont. Dodaj pierwsze konto Cloudflare.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {accounts.map((acc) => (
            <div key={acc.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border">
              <div className="flex items-center gap-2 min-w-0">
                <Cloud className="h-4 w-4 text-orange-400 shrink-0" />
                <span className="font-medium truncate">{acc.label}</span>
                {acc.email && <span className="text-xs text-muted-foreground truncate">· {acc.email}</span>}
                {acc.account_id ? (
                  <Badge variant="outline" className="text-xs">ID ✓</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-400">brak Account ID</Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => setCheckAcc(acc)} title="Odpala każdą funkcję panelu z tym tokenem i pokazuje, czego brakuje">
                <ShieldCheck className="h-4 w-4 mr-1" /> Sprawdź token
              </Button>
              <Button variant="outline" size="sm" onClick={() => openEdit(acc)}>
                <KeyRound className="h-4 w-4 mr-1" /> Edytuj
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Usunąć konto „{acc.label}”?</AlertDialogTitle>
                    <AlertDialogDescription>Znika tylko z panelu (token z bazy). Domeny na Cloudflare zostają nietknięte.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Anuluj</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteAccount(acc.id)}>Usuń</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={Boolean(editAcc)} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edytuj konto — {editAcc?.label}</DialogTitle>
            <DialogDescription>
              Zapisany token nie jest pokazywany (sekret). Żeby go podmienić, wklej nowy i kliknij „Sprawdź” — zobaczysz, które uprawnienia działają. Puste pole tokena = token bez zmian.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cf-edit-label">Nazwa (etykieta)</Label>
              <Input id="cf-edit-label" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf-edit-token">Nowy API Token <span className="text-muted-foreground">(opcjonalnie)</span></Label>
              <div className="flex gap-2">
                <Input id="cf-edit-token" type="password" value={editToken} onChange={(e) => setEditToken(e.target.value)} placeholder="wklej, żeby podmienić" className="font-mono" />
                <Button type="button" variant="outline" onClick={handleEditVerify} disabled={editBusy || !editToken.trim()}>
                  {editBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sprawdź"}
                </Button>
              </div>
              {editCheck.steps && (
                <div className="space-y-2 pt-1">
                  <PermissionBadges perms={editCheck.summary?.permissions ?? null} pending={editCheck.running} />
                  <TokenCheckSteps steps={editCheck.steps} />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf-edit-acc">Account ID</Label>
              <Input id="cf-edit-acc" value={editAccountId} onChange={(e) => setEditAccountId(e.target.value)} placeholder="32 znaki hex — uzupełni się po „Sprawdź”" className="font-mono" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf-edit-email">E-mail konta</Label>
              <Input id="cf-edit-email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit}>Anuluj</Button>
            <Button onClick={handleEditSave} disabled={editBusy || !editLabel.trim()}>
              {editBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TokenCheckDialog account={checkAcc} onClose={() => setCheckAcc(null)} />
    </div>
  );
};
